import { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { SetSaveModal } from '@/components/SetsSelectedData/SetSaveModal';
import api from '@/api/axios';
import { Options } from './Options';
import { Selected } from './Selected';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import Card from '@/components/ui/therapist/Card';
import { useNoInternetDialog } from '@/hooks/useNoInternetDialog';
import { SaveTemplateModal } from '@/components/SaveTemplateModal';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

const modalTitle = {
  wordsets: 'Wörterset speichern',
  sentencesets: 'Sätzeset speichern',
  textsets: 'Texteset speichern',
};

const optionsTitle = {
  wordsets: 'Wörter aus Suche ',
  sentencesets: 'Sätze aus Suche ',
  textsets: 'Texte aus Suche ',
};

const selectedTitle = {
  wordsets: 'Neues Wörterset',
  sentencesets: 'Neues Sätzeset',
  textsets: 'Neues Texteset',
};

const emptyTexts = {
  wordsets: 'Keine Wörter mit diesen Eigenschaften gefunden.',
  sentencesets: 'Keine Sätze mit diesen Eigenschaften gefunden.',
  textsets: 'Keine Texte mit diesen Eigenschaften gefunden.',
};

export function SelectedSetsData({
  data,
  selectedData,
  changeSelectedData,
  patientId,
  page,
  changePage,
  limitModal,
  changeLimitModal,
  setId,
  editData,
  dataKeyName,
  apiKeyName,
  dynamicName,
  isTextSet,
  textLength,
  props,
  type,
  template,
}) {
  const { checkInternet } = useNoInternetDialog();
  const { showNotification } = useNotification();

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [inputState, setInputState] = useState({
    name: template?.name ?? '',
    appDisplayName: '',
  });

  useEffect(() => {
    setInputState((prevState) => ({
      ...prevState,
      name: template?.name ?? '',
    }));
  }, [template]);

  const handleInputChange = useCallback((key) => {
    return (value) => {
      setInputState((prevState) => {
        return {
          ...prevState,
          [key]: value,
        };
      });
    };
  }, []);

  const onOptionClick = useCallback((newData, isSelected, isText) => {
    if (isText) {
      if (!isSelected) {
        changeSelectedData((prevState) => {
          const newDatas = [...prevState, newData];
          if (newDatas.length > 10) {
            changeLimitModal();
            return prevState;
          } else {
            const prevStateIds = prevState.map((prev) => prev._id);
            if (prevStateIds.includes(newData._id)) {
              const clonedState = [...prevState];
              const foundIndex = clonedState.findIndex(
                (value) => value._id == newData._id,
              );
              if (foundIndex >= 0) {
                clonedState[foundIndex] = newData;
                return clonedState;
              } else {
                return [...prevState, newData];
              }
            } else {
              return [...prevState, newData];
            }
          }
        });
      } else {
        changeSelectedData((prevState) => {
          const filteredState = [...prevState].filter((eachData) => {
            if (eachData._id !== newData._id) {
              return true;
            } else {
              if (
                eachData._id === newData._id &&
                eachData.textLength === newData.textLength
              ) {
                return false;
              } else {
                return true;
              }
            }
          });
          return filteredState;
        });
      }
    } else {
      if (!isSelected) {
        changeSelectedData((prevState) => {
          const newDatas = [...prevState, newData];
          if (newDatas.length > 50) {
            changeLimitModal();
            return prevState;
          } else {
            return [...prevState, newData];
          }
        });
      } else {
        changeSelectedData((prevState) => {
          const filteredState = [...prevState].filter((eachData) => {
            return eachData._id !== newData._id;
          });
          return filteredState;
        });
      }
    }
  }, []);

  const onXClick = useCallback((newWord, isText) => {
    onOptionClick(newWord, true, isText);
  }, []);

  const selectAllWords = useCallback(() => {
    changeSelectedData((prevState) => {
      if (isTextSet) {
        const includedIds = {};
        const mergedStates = [...prevState, ...data[dataKeyName]];
        const mergedStatesJSON = mergedStates.map(
          (eachVal) => eachVal._id + 'x' + eachVal.textLength,
        );
        const set = new Set([...mergedStatesJSON]);
        const unduplicatedState = Array.from(set).map((eachVal) =>
          mergedStates.find(
            (obj) =>
              obj._id === eachVal.split('x')[0] &&
              obj.textLength === eachVal.split('x')[1],
          ),
        );

        unduplicatedState.forEach((eachOne) => {
          if (Object.keys(includedIds).includes(eachOne._id)) {
            includedIds[eachOne._id] = includedIds[eachOne._id] + 1;
          } else {
            includedIds[eachOne._id] = 1;
          }
        });

        let newAlteredState = unduplicatedState;
        Object.keys(includedIds).forEach((key) => {
          const value = includedIds[key];
          if (value > 1) {
            newAlteredState = newAlteredState.filter((eachOne) => {
              if (eachOne._id != key) {
                return true;
              } else {
                if (eachOne.textLength == textLength) {
                  return true;
                } else {
                  return false;
                }
              }
            });
          }
        });

        if (newAlteredState.length > 10) {
          changeLimitModal();
          for (let i = newAlteredState.length; i > 10; i--) {
            newAlteredState.pop();
          }
          return newAlteredState;
        } else {
          return newAlteredState;
        }
      } else {
        const mergedStates = [...prevState, ...data[dataKeyName]];
        const mergedStatesJSON = mergedStates.map((eachVal) => eachVal._id);
        const set = new Set([...mergedStatesJSON]);
        const unduplicatedState = Array.from(set).map((eachVal) =>
          mergedStates.find((obj) => obj._id === eachVal),
        );
        if (unduplicatedState.length > 50) {
          changeLimitModal();
          const newState = [...prevState];
          for (let i = 0; i < data[dataKeyName].length; i++) {
            const newData = data[dataKeyName][i];
            if (newState.length === 50) {
              break;
            }
            newState.push(newData);
          }
          return newState;
        } else {
          return unduplicatedState;
        }
      }
    });
  }, [data[dataKeyName]]);

  const clearAllselectedData = useCallback(() => {
    changeSelectedData([]);
  }, []);

  const handleButtonPress = useCallback(() => {
    setIsVisible((prevState) => {
      if (setId && editData != null) {
        setInputState({
          name: editData.name,
          appDisplayName: editData.appDisplayName,
        });
      }
      return !prevState;
    });
  }, [setId, editData]);

  const handleModalAcceptButton = async () => {
    try {
      await checkInternet();

      if (setId) {
        const state = { ...inputState };

        if (type === 'word') {
          if (state.name == '') {
            state.name = 'Neues Wörterset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Wörter';
          }
        } else if (type === 'sentence') {
          if (state.name == '') {
            state.name = 'Neues Sätzeset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Sätze';
          }
        } else {
          if (state.name == '') {
            state.name = 'Neues Texteset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Texte';
          }
        }

        const bodyData = {
          ...editData,
          [`${dynamicName}Items`]: [],
          ...state,
          updatedAt: new Date().toISOString(),
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        await api.put(
          `/api/patients/${patientId}/${apiKeyName}/${setId}?api_version=v2`,
          bodyData,
        );
        showNotification({
          type: 'success',
          title: 'Set erfolgreich gespeichert.',
        });
        props.navigation.goBack();
      } else {
        const state = { ...inputState };
        if (type === 'word') {
          if (state.name == '') {
            state.name = 'Neues Wörterset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Wörter';
          }
        } else if (type === 'sentence') {
          if (state.name == '') {
            state.name = 'Neues Sätzeset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Sätze';
          }
        } else {
          if (state.name == '') {
            state.name = 'Neues Texteset';
          }
          if (state.appDisplayName == '') {
            state.appDisplayName = 'Texte';
          }
        }

        const bodyData = {
          ...state,
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        await api.post(
          `/api/patients/${patientId}/${apiKeyName}?api_version=v2`,
          bodyData,
        );
        showNotification({
          type: 'success',
          title: 'Set erfolgreich gespeichert.',
        });
        props.navigation.goBack();
      }
    } catch (error) {
      logError('', error);
    }
  };

  const maxPage = Math.ceil(data.totalCount / 30);

  const handlePageChange = useCallback((arrowName = null) => {
    return (value = null) => {
      changePage(value, arrowName);
    };
  }, []);

  return (
    <View style={styles.container}>
      <Card style={styles.card}>
        <Options
          title={optionsTitle[apiKeyName]}
          data={data[dataKeyName]}
          isEmptyText={emptyTexts[apiKeyName]}
          selectedData={selectedData.map((eachData) => eachData._id)}
          pureSelectedData={selectedData}
          dataCount={data.totalCount}
          onPress={onOptionClick}
          headerPress={selectAllWords}
          page={page}
          maxPage={maxPage}
          handlePageChange={handlePageChange}
          selectedLimit={
            isTextSet ? selectedData.length === 10 : selectedData.length === 50
          }
          isTextSet={isTextSet}
          textLength={textLength}
        />
      </Card>
      <Card style={styles.card}>
        <Selected
          setId={setId}
          title={(editData?.name || selectedTitle[apiKeyName]) + ' '}
          data={selectedData}
          dataCount={selectedData.length}
          onPress={onXClick}
          headerPress={clearAllselectedData}
          onButtonPress={handleButtonPress}
          isTextSet={isTextSet}
        />
      </Card>
      {props.route.params.templateMode ? (
        <SaveTemplateModal
          mode={props.route.params.templateMode}
          setType={
            apiKeyName === 'wordsets'
              ? 'Word'
              : apiKeyName === 'sentencesets'
                ? 'Sentence'
                : 'Word'
          }
          templateId={template?.id}
          initFormData={template ? { ...template } : undefined}
          isVisible={isVisible}
          newSetItems={selectedData}
          setIsVisible={setIsVisible}
          onCancel={() => setIsVisible(false)}
        />
      ) : (
        <SetSaveModal
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          handleInputChange={handleInputChange}
          handleModalAcceptButton={handleModalAcceptButton}
          inputState={inputState}
          handleButtonPress={handleButtonPress}
          modalTitle={modalTitle[apiKeyName]}
          apiKeyName={apiKeyName}
        />
      )}
      <Modal
        isVisible={limitModal}
        confirmActionButton={
          <Button label="Ok" onPress={() => changeLimitModal(false)} />
        }
        title="Limit erreicht"
        onClose={() => changeLimitModal(false)}
      >
        <Text style={{ fontSize: 20 }}>
          {isTextSet
            ? 'Das Übungsset hat ein Limit von 10 Items.'
            : 'Das Übungsset hat ein Limit von 50 Items.'}
        </Text>
      </Modal>
    </View>
  );
}
